import { useState } from "react"
import { Upload, Download, FileText, Users, Package, Settings, CheckCircle, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"

// 模擬資料
const mockProductData = [
  { id: "P001", model: "精密零件 X100", specs: "100x50x25mm", standardHours: 8, category: "精密加工" },
  { id: "P002", model: "機械組件 Y200", specs: "200x100x50mm", standardHours: 12, category: "機械製造" },
  { id: "P003", model: "電路板 Z300", specs: "150x75x5mm", standardHours: 6, category: "電子組裝" }
]

const mockProcessData = [
  { id: "PR001", process: "CNC加工", department: "加工部", equipment: "CNC-001", capacity: 8 },
  { id: "PR002", process: "組裝作業", department: "組裝部", equipment: "ASM-001", capacity: 12 },
  { id: "PR003", process: "品質檢測", department: "品控部", equipment: "QC-001", capacity: 6 }
]

const mockCustomerData = [
  { id: "C001", name: "科技公司A", contact: "張經理", phone: "02-1234-5678", email: "manager@techA.com", orders: 15 },
  { id: "C002", name: "製造廠B", contact: "李主任", phone: "03-2345-6789", email: "director@factoryB.com", orders: 8 },
  { id: "C003", name: "電子廠C", contact: "王總監", phone: "04-3456-7890", email: "supervisor@electronicsC.com", orders: 22 }
]

const mockScheduleRules = [
  { id: "R001", rule: "高優先級優先", type: "優先級規則", description: "高優先級訂單優先排程" },
  { id: "R002", rule: "產能限制檢查", type: "產能規則", description: "檢查部門日產能限制" },
  { id: "R003", rule: "交期優先", type: "時間規則", description: "接近交期的訂單優先處理" }
]

const importHistory = [
  { date: "2024-01-15", type: "產品資料", file: "products_2024_01.xlsx", status: "成功", records: 156 },
  { date: "2024-01-14", type: "客戶資料", file: "customers_update.csv", status: "成功", records: 89 },
  { date: "2024-01-13", type: "製程資料", file: "process_data.xlsx", status: "失敗", records: 0 },
  { date: "2024-01-12", type: "排程規則", file: "schedule_rules.json", status: "成功", records: 12 }
]

export default function MasterData() {
  const [dragOver, setDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    // 處理檔案上傳邏輯
    const files = Array.from(e.dataTransfer.files)
    console.log('Dropped files:', files)
  }

  return (
    <div className="p-3 md:p-6 bg-secondary/30 min-h-screen">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-foreground">基礎資料管理</h1>
            <p className="text-muted-foreground text-sm md:text-base">匯入和管理系統基礎資料</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" className="gap-2 min-h-[44px]">
              <Download className="h-4 w-4" />
              匯出範本
            </Button>
            <Button className="bg-primary hover:bg-primary-hover min-h-[44px]">
              <Upload className="h-4 w-4 mr-2" />
              批次匯入
            </Button>
          </div>
        </div>
      </div>

      {/* Import Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">產品資料</p>
                <p className="text-2xl font-semibold">1,256</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Settings className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">製程資料</p>
                <p className="text-2xl font-semibold">89</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Users className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">客戶資料</p>
                <p className="text-2xl font-semibold">234</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">排程規則</p>
                <p className="text-2xl font-semibold">45</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* File Upload Area */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">檔案上傳區域</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragOver 
                ? 'border-primary bg-primary/5' 
                : 'border-border hover:border-primary/50 hover:bg-accent/50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">拖放檔案或點擊上傳</h3>
            <p className="text-muted-foreground mb-4">
              支援 Excel (.xlsx, .xls) 和 CSV (.csv) 格式，最大檔案大小 10MB
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline">
                <FileText className="h-4 w-4 mr-2" />
                選擇檔案
              </Button>
              <Button>
                <Upload className="h-4 w-4 mr-2" />
                開始上傳
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Tabs */}
      <Tabs defaultValue="products" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="products">產品資料</TabsTrigger>
          <TabsTrigger value="process">製程資料</TabsTrigger>
          <TabsTrigger value="customers">客戶資料</TabsTrigger>
          <TabsTrigger value="rules">排程規則</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <Card>
            <CardHeader>
              <CardTitle>產品資料管理</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>產品編號</TableHead>
                    <TableHead>產品型號</TableHead>
                    <TableHead>規格</TableHead>
                    <TableHead>標準工時</TableHead>
                    <TableHead>分類</TableHead>
                    <TableHead>操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockProductData.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell className="font-medium">{product.id}</TableCell>
                      <TableCell>{product.model}</TableCell>
                      <TableCell>{product.specs}</TableCell>
                      <TableCell>{product.standardHours}小時</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">編輯</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="process">
          <Card>
            <CardHeader>
              <CardTitle>製程相關資料</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>製程編號</TableHead>
                    <TableHead>工序名稱</TableHead>
                    <TableHead>部門</TableHead>
                    <TableHead>設備</TableHead>
                    <TableHead>產能</TableHead>
                    <TableHead>操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockProcessData.map((process) => (
                    <TableRow key={process.id}>
                      <TableCell className="font-medium">{process.id}</TableCell>
                      <TableCell>{process.process}</TableCell>
                      <TableCell>{process.department}</TableCell>
                      <TableCell>{process.equipment}</TableCell>
                      <TableCell>{process.capacity}件/日</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">編輯</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customers">
          <Card>
            <CardHeader>
              <CardTitle>客戶資料管理</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>客戶編號</TableHead>
                    <TableHead>客戶名稱</TableHead>
                    <TableHead>聯絡人</TableHead>
                    <TableHead>電話</TableHead>
                    <TableHead>電子郵件</TableHead>
                    <TableHead>訂單數</TableHead>
                    <TableHead>操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockCustomerData.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell className="font-medium">{customer.id}</TableCell>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell>{customer.contact}</TableCell>
                      <TableCell>{customer.phone}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.orders}</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">編輯</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules">
          <Card>
            <CardHeader>
              <CardTitle>排程規則資料</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>規則編號</TableHead>
                    <TableHead>規則名稱</TableHead>
                    <TableHead>類型</TableHead>
                    <TableHead>描述</TableHead>
                    <TableHead>狀態</TableHead>
                    <TableHead>操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockScheduleRules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="font-medium">{rule.id}</TableCell>
                      <TableCell>{rule.rule}</TableCell>
                      <TableCell>{rule.type}</TableCell>
                      <TableCell>{rule.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                          啟用
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">編輯</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Import History */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>匯入記錄</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>日期</TableHead>
                <TableHead>資料類型</TableHead>
                <TableHead>檔案名稱</TableHead>
                <TableHead>狀態</TableHead>
                <TableHead>匯入筆數</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {importHistory.map((record, index) => (
                <TableRow key={index}>
                  <TableCell>{record.date}</TableCell>
                  <TableCell>{record.type}</TableCell>
                  <TableCell>{record.file}</TableCell>
                  <TableCell>
                    <Badge 
                      variant="outline" 
                      className={
                        record.status === "成功" 
                          ? "bg-green-100 text-green-800 border-green-200"
                          : "bg-red-100 text-red-800 border-red-200"
                      }
                    >
                      {record.status === "成功" ? (
                        <CheckCircle className="h-3 w-3 mr-1" />
                      ) : (
                        <AlertCircle className="h-3 w-3 mr-1" />
                      )}
                      {record.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{record.records}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}